import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";

type AuditLogEntry = {
	id: string;
	eventType: string;
	actorId: string;
	actorIp: string | null;
	timestamp: string;
	resourceType: string | null;
	resourceId: string | null;
	tenantSlug: string | null;
	actionDetails: string | null;
	method: string | null;
	path: string | null;
	userAgent: string | null;
	responseStatus: number | null;
};

type AuditLogResponse = {
	data: AuditLogEntry[];
	pagination: {
		page: number;
		pageSize: number;
		total: number;
		totalPages: number;
	};
};

export function AdminAuditLogPage() {
	const [page, setPage] = useState(1);
	const [eventType, setEventType] = useState("");
	const [actorId, setActorId] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");

	const params = new URLSearchParams();
	params.set("page", String(page));
	params.set("pageSize", "50");
	if (eventType) params.set("eventType", eventType);
	if (actorId) params.set("actorId", actorId);
	if (startDate) params.set("startDate", startDate);
	if (endDate) params.set("endDate", endDate);

	const { data, loading, error } = useFetch<AuditLogResponse>(
		`/api/admin/audit-log?${params.toString()}`,
	);

	function handleFilter() {
		setPage(1);
	}

	return (
		<div>
			<h1 className="text-2xl font-bold text-gray-900 mb-4">Audit Log</h1>

			<div className="flex flex-wrap gap-3 mb-4 items-end">
				<div>
					<label
						htmlFor="filter-event-type"
						className="block text-xs text-gray-600 mb-1"
					>
						Event Type
					</label>
					<select
						id="filter-event-type"
						value={eventType}
						onChange={(e) => {
							setEventType(e.target.value);
							handleFilter();
						}}
						className="text-sm border border-gray-300 rounded px-2 py-1"
					>
						<option value="">All</option>
						<option value="admin.auth.login">Login</option>
						<option value="admin.auth.signup">Signup</option>
						<option value="admin.auth.logout">Logout</option>
						<option value="admin.auth.password.reset">
							Password Reset (Auth)
						</option>
						<option value="admin.me.access">Me Access</option>
						<option value="admin.metrics.view">Metrics View</option>
						<option value="admin.tenants.list">Tenants List</option>
						<option value="admin.tenant.suspend">Tenant Suspend</option>
						<option value="admin.tenant.activate">Tenant Activate</option>
						<option value="admin.tenant.delete">Tenant Delete</option>
						<option value="admin.tenant.users.list">Tenant Users List</option>
						<option value="admin.tenant.user.sessions">User Sessions</option>
						<option value="admin.tenant.user.password.reset">
							User Password Reset
						</option>
						<option value="admin.impersonation.start">
							Impersonation Start
						</option>
						<option value="admin.impersonation.stop">Impersonation Stop</option>
						<option value="admin.audit.view">Audit View</option>
						<option value="admin.route.access">Route Access</option>
					</select>
				</div>
				<div>
					<label
						htmlFor="filter-actor-id"
						className="block text-xs text-gray-600 mb-1"
					>
						Actor ID
					</label>
					<input
						id="filter-actor-id"
						type="text"
						value={actorId}
						onChange={(e) => {
							setActorId(e.target.value);
							handleFilter();
						}}
						placeholder="Filter by actor"
						className="text-sm border border-gray-300 rounded px-2 py-1"
					/>
				</div>
				<div>
					<label
						htmlFor="filter-start-date"
						className="block text-xs text-gray-600 mb-1"
					>
						Start Date
					</label>
					<input
						id="filter-start-date"
						type="date"
						value={startDate}
						onChange={(e) => {
							setStartDate(e.target.value);
							handleFilter();
						}}
						className="text-sm border border-gray-300 rounded px-2 py-1"
					/>
				</div>
				<div>
					<label
						htmlFor="filter-end-date"
						className="block text-xs text-gray-600 mb-1"
					>
						End Date
					</label>
					<input
						id="filter-end-date"
						type="date"
						value={endDate}
						onChange={(e) => {
							setEndDate(e.target.value);
							handleFilter();
						}}
						className="text-sm border border-gray-300 rounded px-2 py-1"
					/>
				</div>
			</div>

			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}

			{loading && !data && <p className="text-gray-500 text-sm">Loading…</p>}

			{data && (
				<>
					<div className="overflow-x-auto">
						<table className="w-full text-sm text-left">
							<thead className="bg-gray-100 text-gray-700">
								<tr>
									<th className="px-4 py-2">Timestamp</th>
									<th className="px-4 py-2">Event Type</th>
									<th className="px-4 py-2">Actor ID</th>
									<th className="px-4 py-2">Resource</th>
									<th className="px-4 py-2">Tenant</th>
									<th className="px-4 py-2">Method</th>
									<th className="px-4 py-2">Path</th>
									<th className="px-4 py-2">Status</th>
								</tr>
							</thead>
							<tbody>
								{data.data.map((entry) => (
									<tr key={entry.id} className="border-b border-gray-200">
										<td className="px-4 py-2 text-gray-600 whitespace-nowrap">
											{new Date(entry.timestamp).toLocaleString()}
										</td>
										<td className="px-4 py-2">
											<span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
												{entry.eventType}
											</span>
										</td>
										<td className="px-4 py-2 text-gray-600 font-mono text-xs">
											{entry.actorId}
										</td>
										<td className="px-4 py-2 text-gray-600">
											{entry.resourceType
												? `${entry.resourceType}${entry.resourceId ? `:${entry.resourceId}` : ""}`
												: "—"}
										</td>
										<td className="px-4 py-2 text-gray-600">
											{entry.tenantSlug || "—"}
										</td>
										<td className="px-4 py-2 text-gray-600">
											{entry.method || "—"}
										</td>
										<td className="px-4 py-2 text-gray-600 font-mono text-xs max-w-xs truncate">
											{entry.path || "—"}
										</td>
										<td className="px-4 py-2 text-gray-600">
											{entry.responseStatus ?? "—"}
										</td>
									</tr>
								))}
								{data.data.length === 0 && (
									<tr>
										<td
											colSpan={8}
											className="px-4 py-8 text-center text-gray-500"
										>
											No audit log entries found.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					<div className="flex items-center justify-between mt-4">
						<button
							type="button"
							disabled={page <= 1}
							onClick={() => setPage((p) => p - 1)}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Previous
						</button>
						<span className="text-sm text-gray-600">
							Page {data.pagination.page} of {data.pagination.totalPages || 1} (
							{data.pagination.total} entries)
						</span>
						<button
							type="button"
							disabled={page >= data.pagination.totalPages}
							onClick={() => setPage((p) => p + 1)}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Next
						</button>
					</div>
				</>
			)}
		</div>
	);
}
